import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { PACING_OPTIONS, HUMOR_OPTIONS } from '../content/personaOptions';
import {
  getProfile,
  updateProfile,
  checkUsernameAvailability,
  getDigitalLegacy,
} from '../services/apiService';
import IconPicker from '../components/profile/IconPicker';
import ProfileDataField from '../components/profile/ProfileDataField';
import styles from './EditProfilePage.module.css';

function EditProfilePage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [activeTab, setActiveTab] = useState('core');
  const [initialUsername, setInitialUsername] = useState('');
  const [formData, setFormData] = useState(null);
  const [digitalLegacy, setDigitalLegacy] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationStatus, setValidationStatus] = useState('idle');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const [profileData, legacyData] = await Promise.all([
          getProfile(profileId, accessToken),
          getDigitalLegacy(accessToken),
        ]);

        setFormData({
          profileName: profileData.profileName || '',
          username: profileData.username || '',
          overrides: profileData.overrides || {},
          includedData: profileData.includedData || {
            narrative: true,
            expertise: true,
          },
          ctas: profileData.ctas || [],
          welcomeMessage: profileData.welcomeMessage || '',
          promptSuggestions: profileData.promptSuggestions || ['', ''],
        });
        setDigitalLegacy(legacyData);
        setInitialUsername(profileData.username || '');
      } catch (err) {
        setError('Failed to load profile and passport data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [profileId, getAccessTokenSilently]);

  useEffect(() => {
    if (!formData?.username || formData.username === initialUsername) {
      setValidationStatus('idle');
      return;
    }
    setValidationStatus('checking');
    const handler = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(formData.username);
        setValidationStatus(result.available ? 'available' : 'taken');
      } catch (err) {
        setValidationStatus('error');
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [formData?.username, initialUsername]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOverrideChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [field]: value },
    }));
  };

  const handleCommStyleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        communicationStyle: {
          ...prev.overrides.communicationStyle,
          [field]: value,
        },
      },
    }));
  };

  const handleCtaChange = (index, field, value) => {
    setFormData((prev) => {
      const newCtas = [...(prev.ctas || [])];
      if (!newCtas[index]) {
        newCtas[index] = { label: '', url: '', icon: '' };
      }
      newCtas[index][field] = value;
      return { ...prev, ctas: newCtas };
    });
  };

  const handleSuggestionChange = (index, value) => {
    setFormData((prev) => {
      const newSuggestions = [...prev.promptSuggestions];
      newSuggestions[index] = value;
      return { ...prev, promptSuggestions: newSuggestions };
    });
  };

  const addCta = () => {
    setFormData((prev) => {
      const currentCtas = prev.ctas || [];
      if (currentCtas.length >= 3) return prev;
      const newCtas = [...currentCtas, { label: '', url: '', icon: '' }];
      return { ...prev, ctas: newCtas };
    });
  };

  const removeCta = (index) => {
    setFormData((prev) => {
      const newCtas = [...(prev.ctas || [])];
      newCtas.splice(index, 1);
      return { ...prev, ctas: newCtas };
    });
  };

  const handleToggleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      includedData: { ...prev.includedData, [field]: value },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (validationStatus === 'taken') {
      setError('This username is already taken.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const finalOverrides = { ...formData.overrides };
      const finalCtas = (formData.ctas || []).filter(
        (cta) => cta.label && cta.url
      );

      if (!finalOverrides.summary) delete finalOverrides.summary;

      const finalSuggestions = (formData.promptSuggestions || []).filter(
        (s) => s && s.trim() !== ''
      );

      const payload = {
        profileName: formData.profileName,
        username: formData.username,
        overrides: finalOverrides,
        includedData: formData.includedData,
        ctas: finalCtas,
        welcomeMessage: formData.welcomeMessage,
        promptSuggestions: finalSuggestions,
      };
      await updateProfile(profileId, payload, accessToken);
      navigate('/profiles');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !formData) return <p>Loading profile editor...</p>;
  if (error && !isSaving) return <p className="errorText">{error}</p>;

  const getValidationMessage = () => {
    switch (validationStatus) {
      case 'checking':
        return <span className={styles.checking}>Checking...</span>;
      case 'available':
        return <span className={styles.available}>Available!</span>;
      case 'taken':
        return <span className={styles.taken}>Already taken.</span>;
      case 'error':
        return <span className={styles.taken}>Error checking.</span>;
      default:
        return null;
    }
  };

  return (
    <div className={styles.editPageContainer}>
      <div className="page-header">
        <h2>Edit Profile: {formData.profileName}</h2>
        <div>
          <button
            onClick={() => navigate('/profiles')}
            className="action-button secondary"
            style={{ marginRight: '1rem' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button primary"
            disabled={isSaving || validationStatus === 'taken'}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className={styles.tabNav}>
        <button
          className={activeTab === 'core' ? styles.active : ''}
          onClick={() => setActiveTab('core')}
        >
          Core Details
        </button>
        <button
          className={activeTab === 'voice' ? styles.active : ''}
          onClick={() => setActiveTab('voice')}
        >
          Voice & Tone
        </button>
        <button
          className={activeTab === 'content' ? styles.active : ''}
          onClick={() => setActiveTab('content')}
        >
          Content & Knowledge
        </button>
        <button
          className={activeTab === 'actions' ? styles.active : ''}
          onClick={() => setActiveTab('actions')}
        >
          Actions
        </button>
      </div>

      <form onSubmit={handleSave} className={styles.editForm}>
        {activeTab === 'core' && (
          <fieldset>
            <legend>Core Details</legend>
            <div className={styles.formGroup}>
              <label htmlFor="profileName">Profile Name</label>
              <input
                type="text"
                id="profileName"
                name="profileName"
                value={formData.profileName}
                onChange={(e) =>
                  handleFieldChange('profileName', e.target.value)
                }
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="username">Public Username (URL)</label>
              {getValidationMessage()}
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={(e) => handleFieldChange('username', e.target.value)}
              />
            </div>
            <hr className={styles.divider} />
            <ProfileDataField
              label="Summary"
              fieldName="summary"
              type="override"
              passportValue={digitalLegacy?.summary}
              profileValue={formData.overrides.summary}
              onChange={handleOverrideChange}
            />
          </fieldset>
        )}

        {activeTab === 'voice' && (
          <fieldset>
            <legend>Voice & Tone</legend>
            <p className={styles.description}>
              Define a unique voice for this profile, or leave fields blank to
              use the default style from your Digital Passport.
            </p>
            <div className={styles.formGroup}>
              <label htmlFor="welcomeMessage">Custom Welcome Message</label>
              <textarea
                id="welcomeMessage"
                name="welcomeMessage"
                rows="3"
                placeholder="e.g., Hi there! Welcome to my personal AI. How can I help you learn more about me?"
                value={formData.welcomeMessage}
                onChange={(e) =>
                  handleFieldChange('welcomeMessage', e.target.value)
                }
              />
              <small>
                The first message the persona will send to visitors. Leave blank
                for no welcome message.
              </small>
            </div>
            <hr className={styles.divider} />
            <div className={styles.formGroup}>
              <label htmlFor="suggestion1">Custom Suggestion 1</label>
              <input
                type="text"
                id="suggestion1"
                placeholder="e.g., Tell me about your work experience"
                value={formData.promptSuggestions[0]}
                onChange={(e) => handleSuggestionChange(0, e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="suggestion2">Custom Suggestion 2</label>
              <input
                type="text"
                id="suggestion2"
                placeholder="e.g., What are your key skills?"
                value={formData.promptSuggestions[1]}
                onChange={(e) => handleSuggestionChange(1, e.target.value)}
              />
              <small>
                These will appear as clickable prompts for the visitor. Leave
                blank to use defaults.
              </small>
            </div>
            <hr className={styles.divider} />
            <ProfileDataField
              label="Pacing"
              fieldName="pacing"
              type="multiselect"
              options={PACING_OPTIONS}
              passportValue={digitalLegacy?.communicationStyle?.pacing}
              profileValue={formData.overrides.communicationStyle?.pacing}
              onChange={handleCommStyleChange}
            />
            <ProfileDataField
              label="Humor Style"
              fieldName="humorStyle"
              type="multiselect"
              options={HUMOR_OPTIONS}
              passportValue={digitalLegacy?.communicationStyle?.humorStyle}
              profileValue={formData.overrides.communicationStyle?.humorStyle}
              onChange={handleCommStyleChange}
            />
          </fieldset>
        )}

        {activeTab === 'content' && (
          <fieldset>
            <legend>Content & Knowledge</legend>
            <p className={styles.description}>
              Choose which sections of your Digital Passport to include in this
              profile's knowledge base.
            </p>

            <div className={styles.alwaysIncluded}>
              <div>
                <label>Personality Data</label>
                <p>
                  Your core personality traits are always included to ensure
                  authenticity.
                </p>
              </div>
              <span>Always On</span>
            </div>

            <ProfileDataField
              label="Narrative Data"
              description="Includes your biography and personal story."
              fieldName="narrative"
              type="toggle"
              profileValue={formData.includedData.narrative}
              onChange={handleToggleChange}
            />
            <ProfileDataField
              label="Expertise Data"
              description="Includes your professional summary, work history, and skills."
              fieldName="expertise"
              type="toggle"
              profileValue={formData.includedData.expertise}
              onChange={handleToggleChange}
            />
          </fieldset>
        )}

        {activeTab === 'actions' && (
          <fieldset>
            <legend>Call to Action Buttons</legend>
            <p className={styles.description}>
              Add up to 3 custom buttons to your public profile to guide
              visitors. Leave blank to show no buttons.
            </p>

            {(formData.ctas || []).map((cta, index) => (
              <div key={index} className={styles.ctaGroup}>
                <h4>Button {index + 1}</h4>
                <div className={styles.formGroup}>
                  <label htmlFor={`cta-label-${index}`}>Label</label>
                  <input
                    type="text"
                    id={`cta-label-${index}`}
                    placeholder="e.g., Book a Meeting"
                    value={cta.label}
                    onChange={(e) =>
                      handleCtaChange(index, 'label', e.target.value)
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor={`cta-url-${index}`}>URL</label>
                  <input
                    type="url"
                    id={`cta-url-${index}`}
                    placeholder="https://your-link.com"
                    value={cta.url}
                    onChange={(e) =>
                      handleCtaChange(index, 'url', e.target.value)
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor={`cta-icon-${index}`}>Icon</label>
                  <IconPicker
                    value={cta.icon}
                    onChange={(iconValue) =>
                      handleCtaChange(index, 'icon', iconValue)
                    }
                  />
                  <small>Select an icon for your button.</small>
                </div>
                <button
                  type="button"
                  onClick={() => removeCta(index)}
                  className="action-button danger small"
                >
                  Remove Button
                </button>
              </div>
            ))}

            {(formData.ctas?.length || 0) < 3 && (
              <button
                type="button"
                onClick={addCta}
                className="action-button secondary"
                style={{ marginTop: '1rem' }}
              >
                <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
                Add Button
              </button>
            )}
          </fieldset>
        )}
      </form>
    </div>
  );
}

export default withAuthenticationRequired(EditProfilePage);
